/**
 * Emergency Continuity of Operations Plan (COOP) Tab Component
 * Blank, director-fillable COOP template. Save/Print/Download PDF/History only —
 * document upload for this item is handled by the standard Paper Copy on File uploader.
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Printer, Save, History, ChevronLeft, ShieldAlert, Download } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import jsPDF from "jspdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FIELDS = {
  centerName: "",
  reason: "",
  nextUpdateTime: "",
  infrastructurePolicy: "",
  communicationPolicy: "",
  reunificationProtocol: "",
  licensingRequirements: ""
};

export const COOPTab = ({ centerName = "", organization = {} }) => {
  const [view, setView] = useState("form"); // "form" or "history"
  const [savedRecords, setSavedRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [saving, setSaving] = useState(false);

  const [fields, setFields] = useState({
    ...EMPTY_FIELDS,
    centerName: organization?.name || centerName || ""
  });

  const fetchLatestDraft = async () => {
    try {
      const res = await axios.get(`${API}/compliance/emergency-continuity-plan`);
      const latest = res.data?.records?.[0];
      if (latest) {
        setFields(prev => ({ ...prev, ...latest, centerName: latest.centerName || prev.centerName }));
      }
    } catch {
      // silent — form just stays blank
    }
  };

  useEffect(() => {
    fetchLatestDraft();
  }, []); // eslint-disable-line

  useEffect(() => {
    const orgName = organization?.name || centerName || "";
    if (orgName && !fields.centerName) {
      setFields(prev => ({ ...prev, centerName: orgName }));
    }
  }, [organization, centerName]); // eslint-disable-line

  const fetchRecords = async () => {
    setLoadingRecords(true);
    try {
      const res = await axios.get(`${API}/compliance/emergency-continuity-plan`);
      setSavedRecords(res.data?.records || []);
    } catch {
      toast.error("Failed to load saved records");
    } finally {
      setLoadingRecords(false);
    }
  };

  const handleViewHistory = () => {
    setView("history");
    fetchRecords();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/compliance/emergency-continuity-plan`, fields);
      toast.success("Continuity of Operations Plan saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>Continuity of Operations Plan (COOP)</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 30px; color: #1f2937; }
        h1 { font-size: 20px; border-bottom: 2px solid #1e40af; padding-bottom: 8px; }
        h2 { font-size: 15px; color: #1e40af; margin-top: 24px; }
        p.blank { color: #9ca3af; font-style: italic; }
        .template-box { border-left: 4px solid #1e40af; background: #eff6ff; padding: 12px; margin-top: 10px; }
        .footer { margin-top: 30px; font-size: 11px; color: #6b7280; text-align: center; }
      </style></head><body>
      <h1>Continuity of Operations Plan (COOP) &mdash; Emergency Preparedness</h1>
      <p><strong>Center:</strong> ${fields.centerName || '_______________________'}</p>

      <h2>1. Digital Infrastructure &amp; Vital Records Policy</h2>
      <p ${!fields.infrastructurePolicy ? 'class="blank"' : ''}>${fields.infrastructurePolicy || '(Not yet filled in)'}</p>

      <h2>2. Emergency Crisis Communication Channels</h2>
      <p ${!fields.communicationPolicy ? 'class="blank"' : ''}>${fields.communicationPolicy || '(Not yet filled in)'}</p>
      <p><strong>CRISIS ALERT BROADCAST (SMS / EMAIL TEMPLATE)</strong></p>
      <div class="template-box">
        <p><strong>Subject:</strong> URGENT: ${fields.centerName || '[Center Name]'} Emergency Operational Update</p>
        <p>Dear Families,</p>
        <p>We are currently experiencing an operational emergency due to ${fields.reason || '[Insert Reason]'}.</p>
        <p>All children and staff are currently safe and accounted for.</p>
        <p><em>Immediate Action:</em> We are actively executing our emergency safety protocols. Please do not call the front desk directly so our communication networks remain open for emergency personnel.</p>
        <p><em>Next Communication Update:</em> We will send an official update at precisely ${fields.nextUpdateTime || '[Insert Time]'} with details on early dismissal or alternative pickup instructions.</p>
        <p>Thank you for your cooperation and patience.</p>
      </div>

      <h2>3. Step-by-Step Reunification Protocol</h2>
      <p ${!fields.reunificationProtocol ? 'class="blank"' : ''}>${fields.reunificationProtocol || '(Not yet filled in)'}</p>

      <h2>4. Licensing &amp; Regulatory Requirements</h2>
      <p ${!fields.licensingRequirements ? 'class="blank"' : ''}>${fields.licensingRequirements || '(Not yet filled in)'}</p>

      <div class="footer">Confidential &mdash; Child Care Internal Operations Document</div>
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const cn = fields.centerName || organization?.name || 'Childcare Center';
    const pageW = 210; const margin = 15; const contentW = pageW - margin * 2;
    let y = 15;

    const addText = (text, x, size = 10, style = 'normal', color = [0, 0, 0], maxW = contentW) => {
      doc.setFontSize(size); doc.setFont('helvetica', style); doc.setTextColor(...color);
      const lines = doc.splitTextToSize(String(text || ''), maxW);
      lines.forEach(line => {
        if (y > 270) { doc.addPage(); y = 15; }
        doc.text(line, x, y);
        y += size * 0.45;
      });
      y += 1;
    };

    const addSection = (title, content) => {
      if (y > 250) { doc.addPage(); y = 15; }
      addText(title, margin, 11, 'bold', [26, 54, 93]);
      doc.setDrawColor(200, 200, 200); doc.line(margin, y, pageW - margin, y); y += 3;
      addText(content || '(Not yet filled in)', margin, 9, content ? 'normal' : 'italic', content ? [50, 50, 50] : [156, 163, 175]);
      y += 3;
    };

    doc.setFillColor(30, 64, 175);
    doc.rect(margin, y, contentW, 12, 'F');
    doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255);
    doc.text('CONTINUITY OF OPERATIONS PLAN (COOP)', pageW / 2, y + 8, { align: 'center' });
    y += 18;

    doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
    doc.text('Center: ' + cn, margin, y); y += 8;

    addSection('1. Digital Infrastructure & Vital Records Policy', fields.infrastructurePolicy);
    addSection('2. Emergency Crisis Communication Channels', fields.communicationPolicy);
    addSection('3. Step-by-Step Reunification Protocol', fields.reunificationProtocol);
    addSection('4. Licensing & Regulatory Requirements', fields.licensingRequirements);

    doc.setFontSize(8); doc.setTextColor(150, 150, 150);
    doc.text(cn + ' | Continuity of Operations Plan', pageW / 2, 287, { align: 'center' });

    doc.save(`COOP-${cn.replace(/\s+/g, '-')}.pdf`);
  };

  if (view === "history") {
    return (
      <div className="p-4 space-y-4" data-testid="coop-history-view">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => setView("form")} data-testid="coop-back-to-form-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h3 className="font-semibold text-gray-700">Saved COOP Versions</h3>
        </div>
        {loadingRecords ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : savedRecords.length === 0 ? (
          <p className="text-sm text-gray-400" data-testid="coop-history-empty">No saved records yet.</p>
        ) : (
          <div className="space-y-2">
            {savedRecords.map((r) => (
              <div key={r.id} className="border rounded-lg p-3 text-sm" data-testid={`coop-history-record-${r.id}`}>
                <p className="font-medium">{r.centerName}</p>
                <p className="text-xs text-gray-400 mt-1">{new Date(r.createdAt).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4" data-testid="coop-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-blue-700" />
          Continuity of Operations Plan (COOP)
        </h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleViewHistory} data-testid="coop-history-btn">
            <History className="w-4 h-4 mr-1" /> History
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint} data-testid="coop-print-btn">
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadPDF} className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="coop-download-pdf-btn">
            <Download className="w-4 h-4 mr-1" /> Download PDF
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="coop-save-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="coop-preloaded-notice">
        This is a blank Continuity of Operations Plan (COOP) template. Type your center's own procedures into each section below, then Save.
        Print this blank form to include in your inspection records if a standalone COOP is required in your state — confirm with your licensing inspector.
      </div>

      <p className="text-xs text-blue-700 font-medium">Tip: Download this PDF to attach to an email or text message.</p>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm text-blue-800">1. Digital Infrastructure &amp; Vital Records Policy</CardTitle></CardHeader>
        <CardContent>
          <Textarea
            data-testid="coop-infrastructure-input"
            rows={4}
            placeholder="Describe how you back up and access vital records (rosters, emergency cards, staff files) if your primary system or building is unavailable..."
            value={fields.infrastructurePolicy}
            onChange={(e) => setFields({ ...fields, infrastructurePolicy: e.target.value })}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm text-blue-800">2. Emergency Crisis Communication Channels</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            data-testid="coop-communication-input"
            rows={3}
            placeholder="Describe how you will notify parents and staff during an emergency (phone tree, text, email, app notification, etc.)..."
            value={fields.communicationPolicy}
            onChange={(e) => setFields({ ...fields, communicationPolicy: e.target.value })}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Center Name</Label>
              <Input data-testid="coop-center-name-input" value={fields.centerName} onChange={(e) => setFields({ ...fields, centerName: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Reason for Emergency</Label>
              <Input data-testid="coop-reason-input" placeholder="e.g., structural power failure" value={fields.reason} onChange={(e) => setFields({ ...fields, reason: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Time of Next Update</Label>
              <Input data-testid="coop-next-update-input" placeholder="e.g., 3:00 PM" value={fields.nextUpdateTime} onChange={(e) => setFields({ ...fields, nextUpdateTime: e.target.value })} />
            </div>
          </div>
          <div className="border-l-4 border-blue-700 bg-blue-50 p-3 rounded" data-testid="coop-template-preview">
            <p className="font-semibold text-xs text-gray-500 mb-1">CRISIS ALERT BROADCAST (SMS / EMAIL TEMPLATE)</p>
            <p><strong>Subject:</strong> URGENT: {fields.centerName || '[Center Name]'} Emergency Operational Update</p>
            <p className="mt-2">Dear Families,</p>
            <p className="mt-2">We are currently experiencing an operational emergency due to {fields.reason || '[Insert Reason: e.g., structural power failure, severe weather flooding]'}.</p>
            <p className="mt-2">All children and staff are currently safe and accounted for.</p>
            <p className="mt-2"><em>Immediate Action:</em> We are actively executing our emergency safety protocols. Please do not call the front desk directly so our communication networks remain open for emergency personnel.</p>
            <p className="mt-2"><em>Next Communication Update:</em> We will send an official update at precisely {fields.nextUpdateTime || '[Insert Time]'} with details on early dismissal or alternative pickup instructions.</p>
            <p className="mt-2">Thank you for your cooperation and patience.</p>
          </div>
          <p className="text-xs text-gray-400">Printable reference only — send this using your own SMS/email tools outside the app.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm text-blue-800">3. Step-by-Step Reunification Protocol</CardTitle></CardHeader>
        <CardContent>
          <Textarea
            data-testid="coop-reunification-input"
            rows={4}
            placeholder="Describe your child pickup/reunification process during an emergency (check-in gate, ID verification, sign-out log, etc.)..."
            value={fields.reunificationProtocol}
            onChange={(e) => setFields({ ...fields, reunificationProtocol: e.target.value })}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm text-blue-800">4. Licensing &amp; Regulatory Requirements</CardTitle></CardHeader>
        <CardContent>
          <Textarea
            data-testid="coop-licensing-input"
            rows={3}
            placeholder="List any state-specific licensing codes or requirements that apply to your COOP, if applicable..."
            value={fields.licensingRequirements}
            onChange={(e) => setFields({ ...fields, licensingRequirements: e.target.value })}
          />
        </CardContent>
      </Card>

      <p className="text-center text-xs text-gray-400 pt-2">Confidential — Child Care Internal Operations Document</p>
    </div>
  );
};

export default COOPTab;
